import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import { registerSetupCode, resolveBoard, stmt } from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import {
    irSendDefineCodes,
    markIrEsp8266Used,
    markIrRemoteUsed,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/action/helper/ir_helper';
import type { IrSignalData } from '@/(routers)/boards/editor/block/modal/IrSignal';

type Position = keyof (typeof BOARD_CONFIG)[keyof typeof BOARD_CONFIG]['pins'];

const PRESET_RAW_DATA: Record<string, string> = {
    '1': '26,7,26,8,8,25,26,7,9,25,8,25,9,24,8,25,9,25,25,7,9,25,9',
    '2': '26,8,25,8,9,25,24,8,9,25,8,25,8,25,9,25,8,25,8,25,9,25,8',
    '3': '25,8,25,8,9,25,25,8,8,25,25,8,9,25,8,25,8,25,9,25,25,7,26',
    '4': '25,8,26,8,8,25,25,8,9,25,8,25,8,25,25,8,9,25,8,25,8,25,9',
    '5': '25,8,26,7,9,25,25,8,9,24,9,24,9,25,9,24,9,24,9,24,9,25,25',
};

export function register(gen: any): void {
    gen.forBlock['ir_transmitter'] = function (block: Blockly.Block): string {
        const position = block.getFieldValue('POSITION') as Position;
        const signalsJson = block.getFieldValue('IR_SIGNAL') ?? '[]';
        const board = resolveBoard(block);
        const pin = BOARD_CONFIG[board].pins[position].second;

        let signals: IrSignalData[] = [];
        try {
            signals = JSON.parse(signalsJson) as IrSignalData[];
        } catch {
            signals = [];
        }

        if (signals.length === 0) return '';

        const signal = signals[0];

        if (signal.source === 'auto-orange' && board !== 'orange') return '';
        if (signal.source === 'auto-blue' && board !== 'blue') return '';

        const isPreset = ['1', '2', '3', '4', '5'].includes(signal.id);
        const rawData = isPreset
            ? PRESET_RAW_DATA[signal.id]
                  .split(',')
                  .map((v) => Number(v) * 50)
                  .join(',')
            : signal.rawData.join(',');

        if (!rawData) return '';

        const blockId = block.id.replace(/[^a-zA-Z0-9_]/g, '_');
        const rawDataName = `rawData_${pin}_${signal.id.replace(/[^a-zA-Z0-9]/g, '_')}_${blockId}`;

        let code = '';
        if (board === 'blue') {
            markIrEsp8266Used();
            irSendDefineCodes.set(String(pin), `IRsend irsend_${pin}(${pin});\n`);
            registerSetupCode(`irsend_${pin}`, `  irsend_${pin}.begin();\n`);

            code += stmt(`uint32_t ${rawDataName}[] = {${rawData}}`);
            code += stmt(
                `sendRawLarge(irsend_${pin}, ${rawDataName}, sizeof(${rawDataName}) / sizeof(${rawDataName}[0]), 38)`,
            );
        } else {
            markIrRemoteUsed();
            irSendDefineCodes.set(String(pin), `IRsend irsend_${pin}(${pin});\n`);

            code += stmt(`uint16_t ${rawDataName}[] = {${rawData}}`);
            code += stmt(
                `irsend_${pin}.sendRaw(${rawDataName}, sizeof(${rawDataName}) / sizeof(${rawDataName}[0]), 38)`,
            );
        }

        code += stmt(`delay(500)`);

        return code;
    };
}
